/** Identifiants d'édition ITaaS Hospitol — gamme Care / Clinic / Full + Pharmacy (WP-H3). */
export type HospitolEditionId = "care" | "clinic" | "full" | "pharmacy";

/** Profil de provisioning (indépendant du ladder commercial care→clinic→full). */
export type HospitolProvisioningProfile = "hospital" | "pharmacy";

/** Modules UI logiques — masquage non-pharmacie (WP-H3). */
export type HospitolUiModuleId =
  | "dashboard"
  | "admissions"
  | "emergency"
  | "appointments"
  | "patients"
  | "consultations"
  | "prescriptions"
  | "surgery"
  | "nursing"
  | "hospitalization"
  | "pharmacy"
  | "laboratory"
  | "imaging"
  | "rh"
  | "supply"
  | "billing"
  | "insurance"
  | "direction"
  | "admin";

/** Pôles et capacités contrôlés par édition. */
export type HospitolEditionFeature =
  | "pole_medical"
  | "pole_financial_essential"
  | "pole_hr_essential"
  | "pole_diagnostic"
  | "pole_pharmacy"
  | "pole_finances_full"
  | "pole_hr_full"
  | "pole_logistics"
  | "pole_direction"
  | "multi_site"
  | "api_rest"
  | "customizations"
  | "high_availability"
  | "executive_cockpit"
  | "sla_premium";

export interface HospitolEditionDefinition {
  id: HospitolEditionId;
  displayName: string;
  tagline: string;
  /** Idéal pour — type d'établissement */
  idealFor: string[];
  monthlyUsd: number | null;
  maxUsers: number | null;
  maxUsersLabel: string;
  highlight?: boolean;
  /** Pôles inclus dans cette édition */
  poles: string[];
  newInEdition?: string[];
  /** Profil provisioning associé */
  profile: HospitolProvisioningProfile;
}

export const HOSPITOL_BRAND = {
  productName: "ITaaS Hospitol",
  signatureFr: "La plateforme hospitalière intelligente qui simplifie la gestion de votre établissement.",
  signatureEn:
    "The intelligent hospital platform that simplifies how you run your healthcare facility.",
  positioningFr:
    "Digitalisez votre établissement de santé avec une plateforme conçue pour les professionnels.",
  avoidTerm: "logiciel",
  preferTerms: [
    "plateforme hospitalière intelligente",
    "plateforme de gestion hospitalière tout-en-un",
    "écosystème numérique de votre établissement de santé",
  ],
} as const;

/** Services inclus dans toutes les éditions (à mettre en avant sur les supports). */
export const HOSPITOL_INCLUDED_SERVICES = [
  "Hébergement Cloud sécurisé",
  "Sauvegardes automatiques",
  "Mises à jour continues",
  "Assistance technique",
  "Formation initiale",
  "Paramétrage",
  "Accès Web sécurisé",
  "Essai gratuit 30 jours",
] as const;

/** Modules premium vendus en option — toutes éditions. */
export const HOSPITOL_PREMIUM_ADDONS = [
  "Application Patient",
  "Application Médecin",
  "Notifications SMS",
  "WhatsApp",
  "Business Intelligence",
  "IA Assistée",
  "Rapports personnalisés",
] as const;

/**
 * Ladder commercial hospitalier (PEP trial / showcase).
 * Ne pas y ajouter `pharmacy` — SKU sibling (évite promotion PEP non autorisée).
 */
export const HOSPITOL_EDITION_ORDER: readonly HospitolEditionId[] = [
  "care",
  "clinic",
  "full",
] as const;

/** Toutes les éditions provisionnables (hospital ladder + pharmacy). */
export const HOSPITOL_PROVISIONABLE_EDITIONS: readonly HospitolEditionId[] = [
  "care",
  "clinic",
  "full",
  "pharmacy",
] as const;

/** Features activées pour l'édition / profil Pharmacy (allowlist, pas un tier). */
export const HOSPITOL_PHARMACY_FEATURES: readonly HospitolEditionFeature[] = [
  "pole_medical",
  "pole_financial_essential",
  "pole_pharmacy",
] as const;

/**
 * Modules UI autorisés pour Pharmacy.
 * Patients + prescriptions = contexte ordonnances ; pas d'admissions / lab / imaging / RH / etc.
 */
export const HOSPITOL_PHARMACY_ALLOWED_MODULES: readonly HospitolUiModuleId[] = [
  "dashboard",
  "patients",
  "prescriptions",
  "pharmacy",
  "billing",
  "admin",
] as const;

export const HOSPITOL_PHARMACY_MASKED_MODULES: readonly HospitolUiModuleId[] = [
  "admissions",
  "emergency",
  "appointments",
  "consultations",
  "surgery",
  "nursing",
  "hospitalization",
  "laboratory",
  "imaging",
  "rh",
  "supply",
  "insurance",
  "direction",
] as const;

export const HOSPITOL_EDITIONS: Record<HospitolEditionId, HospitolEditionDefinition> = {
  care: {
    id: "care",
    displayName: "Hospitol Care",
    tagline: "Cabinets, centres de santé et petites cliniques",
    idealFor: [
      "Cabinets médicaux",
      "Centres de santé",
      "Polycliniques",
      "Petites cliniques",
    ],
    monthlyUsd: 99,
    maxUsers: 10,
    maxUsersLabel: "Jusqu'à 10 utilisateurs inclus",
    poles: ["Pôle Médical", "Pôle Financier Essentiel", "Pôle RH Essentiel"],
    newInEdition: ["Pôle Médical", "Pôle Financier Essentiel", "Pôle RH Essentiel"],
    profile: "hospital",
  },
  clinic: {
    id: "clinic",
    displayName: "Hospitol Clinic",
    tagline: "Cliniques privées et hôpitaux de district",
    idealFor: [
      "Cliniques privées",
      "Hôpitaux de district",
      "Centres médicaux spécialisés",
    ],
    monthlyUsd: 249,
    maxUsers: 40,
    maxUsersLabel: "Jusqu'à 40 utilisateurs inclus",
    highlight: true,
    poles: [
      "Pôle Médical",
      "Pôle Diagnostic",
      "Pôle Pharmacie",
      "Pôle Finances",
      "Pôle RH",
      "Pôle Logistique",
      "Pôle Direction",
    ],
    newInEdition: [
      "Pôle Diagnostic",
      "Pôle Pharmacie",
      "Pôle Logistique",
      "Pôle Direction",
    ],
    profile: "hospital",
  },
  full: {
    id: "full",
    displayName: "Hospitol Full",
    tagline: "Hôpitaux généraux, multisites et groupes hospitaliers",
    idealFor: [
      "Hôpitaux généraux",
      "Cliniques multisites",
      "Groupes hospitaliers",
      "Hôpitaux universitaires",
    ],
    monthlyUsd: 499,
    maxUsers: null,
    maxUsersLabel: "Utilisateurs illimités",
    poles: [
      "Multi-sites",
      "API",
      "Personnalisations",
      "Haute disponibilité",
      "Cockpit Exécutif",
      "SLA Premium",
    ],
    newInEdition: [
      "Multi-sites",
      "API",
      "Personnalisations",
      "Haute disponibilité",
      "Cockpit Exécutif",
      "SLA Premium",
    ],
    profile: "hospital",
  },
  pharmacy: {
    id: "pharmacy",
    displayName: "ITaaS Pharmacy",
    tagline: "Officines et pharmacies d'établissement — sans modules hospitaliers complets",
    idealFor: [
      "Pharmacies d'officine",
      "Pharmacies hospitalières / cliniques",
      "Points de dispensation",
    ],
    monthlyUsd: 149,
    maxUsers: 15,
    maxUsersLabel: "Jusqu'à 15 utilisateurs inclus",
    poles: ["Pôle Pharmacie", "Pôle Médical essentiel (patients / ordonnances)", "Pôle Financier Essentiel"],
    newInEdition: ["Édition Pharmacy", "Masquage modules non-pharmacie"],
    profile: "pharmacy",
  },
};

export const HOSPITOL_FEATURE_MIN_EDITION: Record<HospitolEditionFeature, HospitolEditionId> = {
  pole_medical: "care",
  pole_financial_essential: "care",
  pole_hr_essential: "care",
  pole_diagnostic: "clinic",
  pole_pharmacy: "clinic",
  pole_finances_full: "clinic",
  pole_hr_full: "clinic",
  pole_logistics: "clinic",
  pole_direction: "clinic",
  multi_site: "full",
  api_rest: "full",
  customizations: "full",
  high_availability: "full",
  executive_cockpit: "full",
  sla_premium: "full",
};

export function isPharmacyEdition(edition: HospitolEditionId | string | null | undefined): boolean {
  return normalizeHospitolEdition(edition) === "pharmacy";
}

/** Care hospital tier (not pharmacy SKU, not clinic/full). */
export function isCareEdition(edition: HospitolEditionId | string | null | undefined): boolean {
  return normalizeHospitolEdition(edition) === "care";
}

export function provisioningProfileForEdition(
  edition: HospitolEditionId,
): HospitolProvisioningProfile {
  return HOSPITOL_EDITIONS[edition].profile;
}

export function normalizeHospitolEdition(value: string | null | undefined): HospitolEditionId {
  const v = value?.trim().toLowerCase();
  if (
    v === "pharmacy" ||
    v === "pharmacy_only" ||
    v === "pharmacy-only" ||
    v === "pharma"
  ) {
    return "pharmacy";
  }
  if (v === "clinic") return "clinic";
  if (v === "full" || v === "hospital" || v === "enterprise") return "full";
  if (
    v === "care" ||
    v === "starter" ||
    v === "per-bed" ||
    v === "essential" ||
    v === "basic"
  ) {
    return "care";
  }
  if (v === "professional" || v === "standard" || v === "premium") return "clinic";
  return "clinic";
}

/** Index ladder hospitalier uniquement — `pharmacy` n'est pas un tier. */
export function editionTierIndex(edition: HospitolEditionId): number {
  if (edition === "pharmacy") return -1;
  return HOSPITOL_EDITION_ORDER.indexOf(edition);
}

export function editionIncludesFeature(
  edition: HospitolEditionId,
  feature: HospitolEditionFeature,
): boolean {
  if (edition === "pharmacy") {
    return (HOSPITOL_PHARMACY_FEATURES as readonly string[]).includes(feature);
  }
  const min = HOSPITOL_FEATURE_MIN_EDITION[feature];
  if (min === "pharmacy") return false;
  return editionTierIndex(edition) >= editionTierIndex(min);
}

export function allowedModulesForEdition(edition: HospitolEditionId): readonly HospitolUiModuleId[] {
  if (edition === "pharmacy") return HOSPITOL_PHARMACY_ALLOWED_MODULES;
  const all: HospitolUiModuleId[] = [
    "dashboard",
    "admissions",
    "emergency",
    "appointments",
    "patients",
    "consultations",
    "prescriptions",
    "surgery",
    "nursing",
    "hospitalization",
    "pharmacy",
    "laboratory",
    "imaging",
    "rh",
    "supply",
    "billing",
    "insurance",
    "direction",
    "admin",
  ];
  if (edition === "care") {
    return all.filter(
      (m) =>
        ![
          "pharmacy",
          "laboratory",
          "imaging",
          "supply",
          "insurance",
          "direction",
          "surgery",
        ].includes(m),
    );
  }
  return all;
}

export function maskedModulesForEdition(edition: HospitolEditionId): readonly HospitolUiModuleId[] {
  if (edition === "pharmacy") return HOSPITOL_PHARMACY_MASKED_MODULES;
  const allowed = new Set(allowedModulesForEdition(edition));
  const all = allowedModulesForEdition("full");
  return all.filter((m) => !allowed.has(m));
}

export function editionDisplayName(edition: HospitolEditionId): string {
  return HOSPITOL_EDITIONS[edition].displayName;
}

export function listEditionsUpTo(edition: HospitolEditionId): HospitolEditionId[] {
  if (edition === "pharmacy") return ["pharmacy"];
  const idx = editionTierIndex(edition);
  return HOSPITOL_EDITION_ORDER.slice(0, idx + 1) as HospitolEditionId[];
}

export function minimumEditionForFeature(feature: HospitolEditionFeature): HospitolEditionId {
  return HOSPITOL_FEATURE_MIN_EDITION[feature];
}

export function upgradeLabelForFeature(feature: HospitolEditionFeature): string {
  return HOSPITOL_EDITIONS[HOSPITOL_FEATURE_MIN_EDITION[feature]].displayName.replace(
    "Hospitol ",
    "",
  ).replace("ITaaS ", "");
}
